import { useCallback, useEffect, useMemo, useState } from "react";
import * as api from "../../api/client";
import { pixelTheme, type Theme } from "../../theme/pixel";
import { defaultBtn, primaryBtn, withBtnStyle } from "../../theme/styles";
import { AnimatedEllipsis } from "./AnimatedEllipsis";
import { SortHeader, thStyle, tdStyle, useSweepJob } from "./balanceShared";

// Deck Power Rankings: Bradley-Terry ratings for every NPC deck,
// starter deck, and a seeded random cohort, all rated together by a
// full round-robin. Ratings are anchored so 0 = the global random-deck
// mean; per-faction medians of the random cohort are reported so the
// EXPECTED win-rate column is "this deck vs the median random deck of
// its own faction" — bounded in (0, 1) regardless of how dominant the
// authored deck is, so the metric doesn't saturate the way raw
// win-rate-vs-random would.

type SortCol = "rating" | "faction_rating" | "name" | "faction" | "source";

const SOURCE_LABEL: Record<api.DeckSource, string> = {
  npc: "NPC",
  starter: "Starter",
  random: "Random",
};

export function DeckBalanceTab() {
  const theme = pixelTheme;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        height: "100%",
        minHeight: 0,
      }}
    >
      <DeckPowerRankings theme={theme} />
    </div>
  );
}

function DeckPowerRankings({ theme }: { theme: Theme }) {
  const [power, setPower] = useState<api.DeckPower | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [factionFilter, setFactionFilter] = useState<Set<string>>(new Set());
  const [sourceFilter, setSourceFilter] = useState<Set<api.DeckSource>>(new Set());
  const [sortCol, setSortCol] = useState<SortCol>("rating");
  const [sortDesc, setSortDesc] = useState(true);
  const [hideRandom, setHideRandom] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await api.listDeckPower();
      setPower(p);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sweep = useSweepJob({
    matches: (j) => j.kind === "deck_power_sweep",
    enqueue: api.enqueueDeckPowerSweep,
    onSuccess: load,
    onError: (err) => {
      // Without surfacing failures, a failed sweep silently resets the
      // button to idle and the admin has no idea what went wrong — the
      // exact failure mode that hid the stale-card-id bug through
      // several click attempts.
      const msg =
        typeof err === "string"
          ? `Sweep failed: ${err}`
          : err instanceof Error
            ? err.message
            : String(err);
      setError(msg);
    },
  });

  const onRunSweep = useCallback(() => {
    setError(null);
    void sweep.run();
  }, [sweep]);

  const allFactions = useMemo(() => {
    if (!power) return [];
    const set = new Set<string>();
    for (const d of power.decks) {
      if (d.faction) set.add(d.faction);
    }
    return Array.from(set).sort();
  }, [power]);

  const allSources = useMemo(() => {
    if (!power) return [] as api.DeckSource[];
    const set = new Set<api.DeckSource>();
    for (const d of power.decks) set.add(d.source);
    return Array.from(set);
  }, [power]);

  const filteredSorted = useMemo(() => {
    if (!power) return [];
    let rows = power.decks.slice();
    if (hideRandom) {
      rows = rows.filter((r) => r.source !== "random");
    }
    if (factionFilter.size > 0) {
      rows = rows.filter((r) => factionFilter.has(r.faction));
    }
    if (sourceFilter.size > 0) {
      rows = rows.filter((r) => sourceFilter.has(r.source));
    }
    const cmp = (a: api.DeckRating, b: api.DeckRating): number => {
      const direction = sortDesc ? -1 : 1;
      switch (sortCol) {
        case "rating":
          return direction * (a.rating - b.rating);
        case "faction_rating": {
          // Decks with no faction rating (singleton-faction case) sort
          // to the end regardless of direction so they don't pollute
          // the head of the column.
          const ar = a.faction_rating;
          const br = b.faction_rating;
          if (ar == null && br == null) return 0;
          if (ar == null) return 1;
          if (br == null) return -1;
          return direction * (ar - br);
        }
        case "name":
          return direction * a.name.localeCompare(b.name);
        case "faction":
          return direction * a.faction.localeCompare(b.faction);
        case "source":
          return direction * a.source.localeCompare(b.source);
        default:
          return 0;
      }
    };
    rows.sort(cmp);
    return rows;
  }, [power, factionFilter, sourceFilter, sortCol, sortDesc, hideRandom]);

  const toggleFaction = useCallback((f: string) => {
    setFactionFilter((prev) => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });
  }, []);

  const toggleSource = useCallback((s: api.DeckSource) => {
    setSourceFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }, []);

  const onHeaderClick = useCallback((col: SortCol) => {
    setSortCol((prev) => {
      if (prev === col) {
        setSortDesc((d) => !d);
        return col;
      }
      setSortDesc(col === "rating" || col === "faction_rating");
      return col;
    });
  }, []);

  if (loading) {
    return <AnimatedEllipsis label="Loading deck rankings" />;
  }
  if (error && !power) {
    return <div style={{ color: theme.accent }}>{error}</div>;
  }

  const sweepRunning = sweep.running;
  const sweepJob = sweep.job;
  const noSweepYet = power?.no_sweep_yet ?? false;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        flex: 1,
        minHeight: 0,
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
        <button
          onClick={onRunSweep}
          disabled={sweepRunning}
          {...withBtnStyle(primaryBtn(theme), { fontSize: 11, padding: "4px 10px" })}
        >
          {sweepRunning
            ? sweepJob?.status === "running"
              ? "SWEEP RUNNING…"
              : "SWEEP QUEUED…"
            : "RUN SWEEP"}
        </button>
        {power && !power.no_sweep_yet && (
          <span style={{ color: theme.inkFaint, fontSize: 11 }}>
            {power.pool_size ?? power.decks.length} decks
            {power.random_per_faction != null && (
              <> · {power.random_per_faction}/faction random</>
            )}
            {power.games_per_pair != null && <> · {power.games_per_pair} games/pair</>}
            {power.elapsed_secs != null && <> · {power.elapsed_secs.toFixed(0)}s</>}
          </span>
        )}
        {error && <span style={{ color: theme.accent, fontSize: 11 }}>{error}</span>}
      </div>

      {(allFactions.length > 0 || allSources.length > 0) && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          {allSources.some((s) => s === "random") && (
            <button
              onClick={() => setHideRandom((v) => !v)}
              {...withBtnStyle(defaultBtn(theme), {
                fontSize: 11,
                padding: "2px 8px",
                background: hideRandom ? theme.accent : "transparent",
                color: hideRandom ? theme.bgDeep : theme.ink,
              })}
            >
              {hideRandom ? "SHOW RANDOM" : "HIDE RANDOM"}
            </button>
          )}
          {allSources.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              <span style={{ color: theme.inkFaint, fontSize: 11, alignSelf: "center" }}>
                SOURCE:
              </span>
              {allSources.map((s) => (
                <button
                  key={s}
                  onClick={() => toggleSource(s)}
                  {...withBtnStyle(defaultBtn(theme), {
                    fontSize: 11,
                    padding: "2px 8px",
                    background: sourceFilter.has(s) ? theme.accent : "transparent",
                    color: sourceFilter.has(s) ? theme.bgDeep : theme.ink,
                  })}
                >
                  {SOURCE_LABEL[s]}
                </button>
              ))}
            </div>
          )}
          {allFactions.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              <span style={{ color: theme.inkFaint, fontSize: 11, alignSelf: "center" }}>
                FACTION:
              </span>
              {allFactions.map((f) => (
                <button
                  key={f}
                  onClick={() => toggleFaction(f)}
                  {...withBtnStyle(defaultBtn(theme), {
                    fontSize: 11,
                    padding: "2px 8px",
                    background: factionFilter.has(f) ? theme.accent : "transparent",
                    color: factionFilter.has(f) ? theme.bgDeep : theme.ink,
                  })}
                >
                  {f}
                </button>
              ))}
            </div>
          )}
          {(factionFilter.size > 0 || sourceFilter.size > 0) && (
            <button
              onClick={() => {
                setFactionFilter(new Set());
                setSourceFilter(new Set());
              }}
              {...withBtnStyle(defaultBtn(theme), { fontSize: 11, padding: "2px 8px" })}
            >
              clear
            </button>
          )}
        </div>
      )}

      {power && power.skipped_authored && power.skipped_authored.length > 0 && (
        <div
          style={{
            color: "#c54a3b",
            fontSize: 11,
            padding: "6px 8px",
            border: `1px solid ${theme.panelEdge}`,
            background: theme.panelSolid,
          }}
        >
          <strong>{power.skipped_authored.length} deck(s) skipped</strong> —
          fix the underlying YAML and re-run the sweep:
          <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
            {power.skipped_authored.map((s) => (
              <li key={s.id}>
                <code>{s.id}</code> ({s.faction}): {s.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {noSweepYet ? (
        <div style={{ color: theme.inkFaint, fontSize: 13 }}>
          No sweep yet. Click <strong>RUN SWEEP</strong> to rate every deck.
        </div>
      ) : (
        <div
          style={{
            minHeight: 0,
            overflowY: "auto",
            border: `1px solid ${theme.panelEdge}`,
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: theme.bgDeep,
                zIndex: 1,
              }}
            >
              <tr>
                <SortHeader
                  theme={theme}
                  active={sortCol === "name"}
                  onClick={() => onHeaderClick("name")}
                >
                  DECK
                </SortHeader>
                <SortHeader
                  theme={theme}
                  active={sortCol === "source"}
                  onClick={() => onHeaderClick("source")}
                >
                  SOURCE
                </SortHeader>
                <SortHeader
                  theme={theme}
                  active={sortCol === "faction"}
                  onClick={() => onHeaderClick("faction")}
                >
                  FACTION
                </SortHeader>
                <SortHeader
                  theme={theme}
                  active={sortCol === "rating"}
                  onClick={() => onHeaderClick("rating")}
                  align="right"
                >
                  GLOBAL {sortCol === "rating" ? (sortDesc ? "↓" : "↑") : ""}
                </SortHeader>
                <SortHeader
                  theme={theme}
                  active={sortCol === "faction_rating"}
                  onClick={() => onHeaderClick("faction_rating")}
                  align="right"
                >
                  FACTION {sortCol === "faction_rating" ? (sortDesc ? "↓" : "↑") : ""}
                </SortHeader>
                <th style={thStyle(theme, "right")}>GAMES</th>
              </tr>
            </thead>
            <tbody>
              {filteredSorted.map((d) => (
                <DeckRow key={d.id} entry={d} theme={theme} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DeckRow({ entry, theme }: { entry: api.DeckRating; theme: Theme }) {
  const isRandom = entry.source === "random";
  // Same green/red tint scale on both rating columns since they share
  // units (BT log-odds). Random rows are dimmed and uncolored so the
  // admin's eye lands on authored decks.
  const tint = (v: number | null | undefined): string => {
    if (isRandom || v == null) return theme.inkFaint;
    if (Math.abs(v) <= 0.2) return theme.ink;
    return v < 0 ? "#c54a3b" : "#3aa256";
  };
  const baseColor = isRandom ? theme.inkFaint : theme.ink;
  const rowStyle: React.CSSProperties = {
    borderBottom: `1px solid ${theme.panelEdge}`,
    opacity: isRandom ? 0.55 : 1,
    fontStyle: isRandom ? "italic" : undefined,
  };
  const cell: React.CSSProperties = { ...tdStyle(theme), color: baseColor };
  const fmt = (v: number | null | undefined): string =>
    v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(2)}`;

  return (
    <tr style={rowStyle}>
      <td style={cell} title={entry.id}>
        {entry.name}
      </td>
      <td style={cell}>{SOURCE_LABEL[entry.source]}</td>
      <td style={cell}>{entry.faction}</td>
      <td
        style={{
          ...cell,
          textAlign: "right",
          color: tint(entry.rating),
          fontWeight: isRandom ? 400 : 600,
        }}
        title="Global Bradley-Terry log-strength (0 = mean random deck across the whole pool). Comparable across factions."
      >
        {fmt(entry.rating)}
      </td>
      <td
        style={{
          ...cell,
          textAlign: "right",
          color: tint(entry.faction_rating),
          fontWeight: isRandom ? 400 : 600,
        }}
        title="Per-faction Bradley-Terry log-strength (0 = mean random deck of this faction). Comparable only within a faction."
      >
        {fmt(entry.faction_rating)}
      </td>
      <td style={{ ...cell, textAlign: "right", color: theme.inkFaint }}>
        {entry.games_played}
      </td>
    </tr>
  );
}

